import * as Device from 'expo-device';

/**
 * Whether the web app is being viewed on a phone or a tablet, in which case we
 * point the visitor at the HiNote Life mobile app instead of the TV UI (see
 * components/MobileGate.tsx).
 *
 * No single browser signal answers this, so we stack five, strongest first.
 * Every one of them is fixed for the page load — viewport size is deliberately
 * never consulted, so resizing a window or rotating a phone cannot flip the
 * answer mid-session.
 *
 * ./README.md explains each step, why it's needed, and how to test it.
 */

// Compared against the screen's SHORT edge, so orientation doesn't matter.
// Inclusive: the 12.9" iPad Pro is 1024 x 1366 CSS px.
const MAX_HANDHELD_SHORT_EDGE_PX = 1024;

// Chromium-only. Safari and Firefox declined to implement it, so it is absent
// on every browser on iOS and its absence tells us nothing.
type NavigatorWithUserAgentData = Navigator & {
  userAgentData?: { mobile?: boolean };
};

function detectMobileWebDevice(): boolean {
  // Televisions bail out first. Android TV, Google TV and Fire TV all carry
  // "Android" in their user agent and would otherwise be read as tablets.
  if (Device.deviceType === Device.DeviceType.TV) {
    return false;
  }

  // The only purpose-built signal, where it exists. It reports false on Android
  // tablets, so a false here is not a "no" — only a true is conclusive.
  const { userAgentData } = navigator as NavigatorWithUserAgentData;
  if (userAgentData?.mobile === true) {
    return true;
  }

  // expo-device's web shim parses the user agent with ua-parser-js. This is the
  // branch that catches iOS Safari and Firefox, neither of which ships
  // userAgentData, and it uses a maintained device database rather than a
  // regex we'd have to keep up to date ourselves.
  if (
    Device.deviceType === Device.DeviceType.PHONE ||
    Device.deviceType === Device.DeviceType.TABLET
  ) {
    return true;
  }

  // iPadOS 13+ Safari reports itself as "Macintosh; Intel Mac OS X" by default
  // and is indistinguishable from a Mac by user agent alone. Real Macs report
  // maxTouchPoints: 0.
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
    return true;
  }

  // Catch-all for "Request desktop site" — which deliberately lies about the
  // user agent, defeating every check above — and for devices ua-parser-js
  // doesn't recognize. Input capability is not spoofed by that toggle: these
  // queries describe the PRIMARY pointer, so a touchscreen laptop still reports
  // a fine pointer and stays out. `screen` (not `window`) because the physical
  // screen doesn't change when a window is resized.
  const isTouchPrimary =
    window.matchMedia?.('(pointer: coarse)').matches === true &&
    window.matchMedia?.('(hover: none)').matches === true;
  const screenShortEdge = Math.min(window.screen.width, window.screen.height);

  return isTouchPrimary && screenShortEdge <= MAX_HANDHELD_SHORT_EDGE_PX;
}

/**
 * Manual override for testing: ?mobileGate=on | off | auto.
 *
 * A URL param is the only channel that reaches a gated device, since MobileGate
 * wraps the whole app including /debug. Mirrored into sessionStorage because
 * expo-router drops the param on navigation, so a reload mid-test would
 * otherwise slam the gate shut; sessionStorage rather than localStorage so a
 * forced state can't outlive the tab. See ./README.md.
 */
const OVERRIDE_STORAGE_KEY = 'MOBILE_GATE_OVERRIDE';

function readOverride(): boolean | null {
  try {
    const param = new URLSearchParams(window.location.search).get('mobileGate');

    if (param === 'on' || param === 'off') {
      sessionStorage.setItem(OVERRIDE_STORAGE_KEY, param);
    } else if (param === 'auto') {
      sessionStorage.removeItem(OVERRIDE_STORAGE_KEY);
    }

    const stored = sessionStorage.getItem(OVERRIDE_STORAGE_KEY);
    if (stored === 'on') return true;
    if (stored === 'off') return false;
    return null;
  } catch {
    // Storage throws in Safari private mode and with third-party cookies
    // blocked. Same guard CodeGate uses.
    return null;
  }
}

export type MobileGateSource = 'detected' | 'forced-on' | 'forced-off';

function resolve(): { isMobile: boolean; source: MobileGateSource } {
  try {
    // Every path below touches the DOM, so guard the single entry point. Moot
    // under `output: 'single'` (this bundle only ever evaluates in a browser),
    // but switching web to `output: 'static'` would evaluate module scope in Node
    // during prerender, where window doesn't exist.
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return { isMobile: false, source: 'detected' };
    }

    const override = readOverride();
    if (override !== null) {
      return {
        isMobile: override,
        source: override ? 'forced-on' : 'forced-off',
      };
    }

    return { isMobile: detectMobileWebDevice(), source: 'detected' };
  } catch (err) {
    console.warn(
      'Failed to resolve mobile web device status, defaulting to TV UI',
      err,
    );
    // Defensive: if any of the above throws, don't gate the user out of the app.
    return { isMobile: false, source: 'detected' };
  }
}

// Resolved once at module scope, never as a hook: the gate must not be able to
// toggle while someone is using the app.
const resolved = resolve();

export const isMobileWebDevice = resolved.isMobile;

// Exposed so a forced session is visible on the debug screen and doesn't get
// mistaken for a real misclassification in Sentry.
export const mobileGateSource = resolved.source;
